import { create } from 'zustand'
import { supabase } from '../lib/supabase'

const HOUSEHOLD_KEY = 'dc_household_id'

async function loadUserHouseholds(userId, set) {
  const { data, error } = await supabase
    .from('household_members')
    .select('role, joined_at, households(id, name, created_by, created_at)')
    .eq('user_id', userId)

  if (error || !data?.length) {
    set({ households: [], household: null, needsOnboarding: true, isLoading: false })
    return
  }

  const households = data.map((m) => ({ ...m.households, userRole: m.role }))
  const savedId = localStorage.getItem(HOUSEHOLD_KEY)
  const current = households.find((h) => h.id === savedId) ?? households[0]

  localStorage.setItem(HOUSEHOLD_KEY, current.id)
  set({ households, household: current, needsOnboarding: false, isLoading: false })
}

export const useAuthStore = create((set, get) => ({
  user:            null,
  session:         null,
  household:       null,   // currently active household object (with userRole)
  households:      [],     // all households the user belongs to
  isLoading:       true,
  needsOnboarding: false,

  setUser:      (user)      => set({ user }),
  setSession:   (session)   => set({ session }),
  setHousehold: (household) => {
    if (household?.id) localStorage.setItem(HOUSEHOLD_KEY, household.id)
    set({ household })
  },
  setHouseholds: (households) => set({ households }),

  // Call after creating a household in onboarding to refresh store state
  refreshHouseholds: async () => {
    const { user } = get()
    if (!user) return
    await loadUserHouseholds(user.id, set)
  },

  switchHousehold: (householdId) => {
    const { households } = get()
    const h = households.find((hh) => hh.id === householdId)
    if (h) {
      localStorage.setItem(HOUSEHOLD_KEY, h.id)
      set({ household: h })
    }
  },

  signOut: async () => {
    localStorage.removeItem(HOUSEHOLD_KEY)
    await supabase.auth.signOut()
    set({ user: null, session: null, household: null, households: [], needsOnboarding: false })
  },

  initAuth: () => {
    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        set({ session, user: session?.user ?? null })
        if (session?.user) {
          loadUserHouseholds(session.user.id, set)
        } else {
          set({ isLoading: false })
        }
      })
      .catch(() => set({ isLoading: false }))

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        set({ session, user: session?.user ?? null })
        if (session?.user) {
          loadUserHouseholds(session.user.id, set)
        } else {
          set({
            households:      [],
            household:       null,
            needsOnboarding: false,
            isLoading:       false,
          })
        }
      }
    )

    return () => subscription.unsubscribe()
  },
}))
